import React, {useState, useEffect} from "react"

import FormattedMessage, {InjectIntl, injectIntl} from "@irati/utils/lang"
import {Body, H2} from "@irati/ui/src/Typography"
import mainTheme from "@irati/ui/src/global/theme"
import Button from "@irati/ui/src/Button"
import Flex from "@irati/ui/src/Flex"
import GenericModal from "@irati/ui/src/GenericModal"
import {TextArea, Radio} from "@irati/ui/src/FormItem"
import {Option} from "@irati/ui/src/FormItem/Radio/Radio"
import validation from "@irati/utils/validation"
import Spacing from "@irati/ui/src/Spacing"
import {CancellationReason, ZuoraCurrentPlanWindow} from "@irati/types"
import adminTracking, {RenewalPlanSource, RenewalPlanType} from "@irati-tracking/admin/admin"

const CANCELLATION_REASONS: Option[] = [
  {
    value: CancellationReason.EmployeesNotUsing,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item1"
  },
  {
    value: CancellationReason.LimitedGlobalOfferings,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item2"
  },
  {
    value: CancellationReason.HighPrice,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item3"
  },
  {
    value: CancellationReason.BudgetCuts,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item4"
  },
  {
    value: CancellationReason.MissingFeature,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item5"
  },
  {
    value: CancellationReason.OfferFromCompetitor,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item6"
  },
  {
    value: CancellationReason.CustomerServiceNotGreat,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item7"
  },
  {
    value: CancellationReason.BusinessDownsizeOrClose,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item8"
  },
  {
    value: CancellationReason.ManagementChanging,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item9"
  },
  {
    value: CancellationReason.TakingABreak,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item10"
  },
  {
    value: CancellationReason.Other,
    label: "admin.billing.currentPlan.cancellationModal.reasons.item11"
  }
]

interface Props extends InjectIntl {
  show: boolean
  renewalWindow?: ZuoraCurrentPlanWindow
  onHide: (ignoreTracking: boolean) => void
  onPrevious: () => void
  onNext: (reason?: CancellationReason, reasonText?: string) => void
  source: RenewalPlanSource
}

const CancellationModal: React.FC<Props> = ({show, renewalWindow, onHide, onPrevious, onNext, source, formatMessage}) => {
  const [showModal, setShowModal] = useState<boolean>(show)
  const [selectedReason, setSelectedReason] = useState<CancellationReason>()
  const [otherReason, setOtherReason] = useState<string>()

  const validate = validation
    .string(formatMessage({id: "admin.billing.currentPlan.cancellationModal.isRequired"}))
    .test(
      "notJustSpaces",
      formatMessage({id: "admin.billing.currentPlan.cancellationModal.notJustSpaces"}),
      (input: string) => input && input.trim().length !== 0
    )

  const reset = () => {
    setSelectedReason(undefined)
    setOtherReason(undefined)
    onHide(true)
  }

  useEffect(() => {
    setShowModal(show)
  }, [show])

  const handleConfirm = () => {
    adminTracking.renewalPlan({
      stage: 3,
      type: RenewalPlanType.cancellationFlow,
      reason: selectedReason,
      action: "nextCancelReason",
      source
    })
    onNext(selectedReason, otherReason)
  }

  return (
    <GenericModal customWidth={800} hideActions show={showModal} onHide={reset}>
      <H2 marginTop={2} marginBottom={3} color={mainTheme.colors.filterChip}>
        {renewalWindow === ZuoraCurrentPlanWindow.RenewalWindowWithoutCancellation ? (
          <FormattedMessage id="admin.billing.currentPlan.cancellationModal.outside90Days.title" />
        ) : (
          <FormattedMessage id="admin.billing.currentPlan.cancellationModal.header" />
        )}
      </H2>
      {renewalWindow === ZuoraCurrentPlanWindow.RenewalWindow && (
        <Flex minWidth="700px" direction="column" marginBottom={3}>
          <Radio
            name="cancellations_reasons"
            value={selectedReason}
            onChange={(value) => setSelectedReason(value)}
            options={CANCELLATION_REASONS.map((item) => {
              return {...item, label: formatMessage({id: item.label})}
            })}
          />
          <Spacing paddingLeft={3}>
            <TextArea
              hideLabel
              name="other_reason"
              onBlur={() => setSelectedReason(CANCELLATION_REASONS[CANCELLATION_REASONS.length - 1].value)}
              value={otherReason}
              onChange={(value) => setOtherReason(value)}
              validation={selectedReason === CANCELLATION_REASONS[CANCELLATION_REASONS.length - 1].value ? validate : null}
            />
          </Spacing>
        </Flex>
      )}
      {renewalWindow === ZuoraCurrentPlanWindow.RenewalWindowWithoutCancellation && (
        <Body>
          <FormattedMessage id="admin.billing.currentPlan.cancellationModal.outside90Days.text" />
        </Body>
      )}
      {renewalWindow === ZuoraCurrentPlanWindow.RenewalWindowWithoutCancellation ? (
        <Flex justifyContent="center">
          <Button primary onClick={onPrevious}>
            <FormattedMessage id="admin.billing.currentPlan.cancellationModal.outside90Days.btn" />
          </Button>
        </Flex>
      ) : (
        <Flex alignItems="center">
          <Button marginRight onClick={onPrevious}>
            <FormattedMessage id="admin.billing.currentPlan.cancellationModal.btn.previous" />
          </Button>
          <Button primary onClick={handleConfirm} disabled={!selectedReason}>
            <FormattedMessage id="admin.billing.currentPlan.cancellationModal.btn.next" />
          </Button>
        </Flex>
      )}
    </GenericModal>
  )
}

export default injectIntl(CancellationModal)
